import { photoCaptureServiceModule } from '../photo-capture-service/photo-capture-service';

// Webcams need a beat to calibrate brightness before the first usable frame.
export const CAMERA_WARMUP_MS = 1000;
import { photoStorageServiceModule } from '../photo-storage-service/photo-storage-service';

export const photoManagerServiceModule = angular
  .module('Q.services.photo-manager-service', [
    photoCaptureServiceModule,
    photoStorageServiceModule,
  ])

  .factory(
    'photoManagerService',
    function (photoCaptureService, photoStorageService, $q, $timeout) {
      const WELCOME_PHOTO_RETRY_MS = 1000;

      // Take a photo in 4 to 9 minute intervals during the assessment
      const LOWER_BOUND_ASSESSMENT_PHOTO_MS = 4 * 60 * 1000;
      const UPPER_BOUND_ASSESSMENT_PHOTO_MS = 9 * 60 * 1000;

      // Flag brightness levels outside of the 20-80% range
      const LOWER_BOUND_BRIGHTNESS = 0.2;
      const UPPER_BOUND_BRIGHTNESS = 0.8;

      /**
       * Compares two image data objects to check if they are identical within a threshold.
       * @param {ImageData} a - The first image data object.
       * @param {ImageData} b - The second image data object.
       * @param {number} threshold - The maximum allowed number of differing pixels.
       * @returns {boolean} True if the images are considered identical, false otherwise.
       */
      const imagesEqual = (a, b, threshold = 0) => {
        if (!a || !b || a.width !== b.width || a.height !== b.height) {
          return false;
        }

        let differingPixels = 0;

        for (let i = 0; i < a.data.length; i++) {
          if (a.data[i] !== b.data[i] && ++differingPixels > threshold) {
            return false;
          }
        }

        return true;
      };

      const build = () => {
        const photosTaken = [];
        let lastPhotoData;
        let assessmentResultId;
        let teamCandidateId;
        let assessmentPhotoTimeout;
        let welcomePhotoRetryTimeout;
        let onError;
        let startPromise;

        const photoCapture = photoCaptureService.build({
          onEnd: () => {
            $timeout.cancel(welcomePhotoRetryTimeout);
            $timeout.cancel(assessmentPhotoTimeout);

            if (onError) {
              onError({
                message: 'Feed ended unexpectedly',
              });
            }
          },
        });

        // running() stays false for the whole getUserMedia window, so the Start Webcam button is
        // still showing while a reload's resume is coming up. A second press would restart the
        // camera - leaking the first stream, since stop() has no stream to release yet - and
        // reschedule the capture loop past the resumed session's first photo.
        const start = (opts = {}) => {
          assessmentResultId = opts.assessmentResultId || assessmentResultId;
          teamCandidateId = opts.teamCandidateId || teamCandidateId;

          if (startPromise) {
            return startPromise;
          }

          if (photoCapture.running()) {
            return $q.when();
          }

          startPromise = photoCapture.start().finally(() => {
            startPromise = null;
          });

          return startPromise;
        };

        const takePhoto = () => {
          if (!assessmentResultId) {
            const msg =
              'Proctoring Manager: no assessmentResultId was set when attempting to take a photo';
            return $q.reject(msg);
          }

          if (!teamCandidateId) {
            const msg =
              'Proctoring Manager: no teamCandidateId was set when attempting to take a photo';
            return $q.reject(msg);
          }

          const newPhotoData = photoCapture.imageData();
          const isStatic =
            !!lastPhotoData && imagesEqual(lastPhotoData, newPhotoData, 100);
          lastPhotoData = newPhotoData;

          let brightness;
          try {
            brightness = photoCapture.brightness();
          } catch (err) {
            return $q.reject(err);
          }

          return photoStorageService
            .uploadPhoto({
              dataURL: photoCapture.takePhoto(),
              assessmentResultId,
              teamCandidateId,
              isStatic,
              isDim: brightness < LOWER_BOUND_BRIGHTNESS,
              isBright: brightness > UPPER_BOUND_BRIGHTNESS,
            })
            .then((photoFileData) => {
              photosTaken.push(photoFileData);
              return $q.when(photoFileData);
            });
        };

        // Retries until a photo sticks, and never rejects - so nothing can be sequenced after it.
        const takeWelcomePhoto = () => {
          $timeout.cancel(welcomePhotoRetryTimeout);
          const tryTakingPhotoUntilOK = (resolve) => {
            takePhoto()
              .then(resolve)
              .catch((err) => {
                if (onError) {
                  onError(err);
                }

                welcomePhotoRetryTimeout = $timeout(
                  () => tryTakingPhotoUntilOK(resolve),
                  WELCOME_PHOTO_RETRY_MS,
                );
              });
          };
          return $q(tryTakingPhotoUntilOK);
        };

        const nextPhotoDelayMs = () =>
          Math.random() *
            (UPPER_BOUND_ASSESSMENT_PHOTO_MS -
              LOWER_BOUND_ASSESSMENT_PHOTO_MS) +
          LOWER_BOUND_ASSESSMENT_PHOTO_MS;

        // The first photo is an interval away because takeWelcomePhoto normally covers the start
        // of the session, and two captures in one beat upload near-identical frames. A resumed
        // session has no welcome photo, so it asks for one up front.
        const takeAssessmentPhotos = ({ coversSessionStart = false } = {}) => {
          $timeout.cancel(assessmentPhotoTimeout);

          const firstPhotoMs = coversSessionStart
            ? CAMERA_WARMUP_MS
            : nextPhotoDelayMs();

          assessmentPhotoTimeout = $timeout(() => {
            takePhoto().catch((err) => {
              if (onError) {
                onError(err);
              }
            });
            takeAssessmentPhotos();
          }, firstPhotoMs);
        };

        return {
          start,
          takeWelcomePhoto,
          takeAssessmentPhotos,
          running: () => photoCapture.running(),
          starting: () => !!startPromise,
          photosTaken: () => photosTaken,
          // Stored, not merely attempted - the start button gates on this.
          hasStoredPhoto: () => photosTaken.length > 0,
          setErrorHandler: (errorHandlerFn) => {
            onError = errorHandlerFn;
          },
          stop: () => {
            $timeout.cancel(welcomePhotoRetryTimeout);
            $timeout.cancel(assessmentPhotoTimeout);
            photoCapture.stop();
          },
          newExternalVideo: () => photoCapture.newExternalVideo(),
        };
      };

      return { build };
    },
  );
